import React, { useState, useRef, useMemo, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CalendarDays, Check, Play, X } from 'lucide-react';
import { WeekViewIcon } from './WeekViewIcon';
import { WorkoutSession } from '../types/dataModel';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './StickyWorkoutCalendar.css';

export type CalendarViewState = {
  viewMode: 'week' | 'month';
  weekDate: string; // YYYY-MM-DD
  monthDate: string; // YYYY-MM-DD
};

function parseDateKey(key: string): Date {
  const d = new Date(key + 'T00:00:00');
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Format a Date as YYYY-MM-DD in local time (avoids UTC shift from toISOString) */
function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface StickyWorkoutCalendarHandle {
  viewMode: 'week' | 'month';
  toggleViewMode: () => void;
  goToToday: () => void;
  isShowingCurrentWeek: boolean;
}

interface StickyWorkoutCalendarProps {
  sessions: (WorkoutSession & { workout?: any })[];
  onDateClick: (date: string, sessions: (WorkoutSession & { workout?: any })[]) => void;
  /** Called when user navigates to a different month (so parent can fetch more if outside loaded range) */
  onDisplayedMonthChange?: (year: number, month: number) => void;
  /** Called when displayed date range changes (start/end YYYY-MM-DD). Use to fetch when range is outside loaded data (week view: Mon–Sun; month view: 1st–last). */
  onDisplayedRangeChange?: (start: string, end: string) => void;
  /** Optional initial state (e.g. restored from sessionStorage when returning from workout details) */
  initialViewState?: CalendarViewState | null;
  /** Called when calendar view changes so parent can persist state */
  onCalendarStateChange?: (state: CalendarViewState) => void;
  /** When 'light', use dark text for use on white/frosted background */
  variant?: 'gradient' | 'light';
  /** Called when user taps the Today button (so parent can sync selected date) */
  onTodayClick?: () => void;
  /** Currently selected calendar date (YYYY-MM-DD) for FR-1.7: black box on selected, grey box on today when not selected */
  selectedDate?: string;
  /** When true, header row (month name, Today, calendar icon) is hidden – parent renders controls (e.g. in same row as app logo) */
  hideHeaderRow?: boolean;
  /** When true, only weekly view is shown and the view toggle icon is hidden */
  weekViewOnly?: boolean;
  /** When false, the calendar container is not sticky (scrolls with content) */
  sticky?: boolean;
}

type ViewMode = 'week' | 'month';

export const StickyWorkoutCalendar = forwardRef<StickyWorkoutCalendarHandle, StickyWorkoutCalendarProps>(({ 
  sessions, 
  onDateClick,
  onDisplayedMonthChange,
  onDisplayedRangeChange,
  initialViewState,
  onCalendarStateChange,
  variant = 'gradient',
  onTodayClick,
  selectedDate,
  hideHeaderRow = false,
  weekViewOnly = false,
  sticky = true,
}, ref) => {
  const isLight = variant === 'light';
  const textClass = isLight ? 'text-theme-text' : 'text-white';
  const textMutedClass = isLight ? 'text-theme-text/80' : 'text-white/90';
  const todayBtnClass = 'px-3 py-1 text-xs font-medium text-white bg-theme-bg-dark hover:opacity-90 rounded-lg transition-colors shadow-sm';
  const cellBorderClass = isLight ? 'border-theme-bg-dark' : 'border-white';
  const cellDefaultTextClass = isLight ? 'text-theme-text' : 'text-white';
  const dotScheduledClass = isLight ? 'text-gray-500' : 'text-gray-400';
  const getInitialDate = (key: string | undefined, fallback: () => Date) => {
    if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return fallback();
    try {
      return parseDateKey(key);
    } catch {
      return fallback();
    }
  };
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Separate state for each view mode; allow initial state from parent (e.g. restored from sessionStorage)
  const [weekDate, setWeekDate] = useState(() =>
    initialViewState ? getInitialDate(initialViewState.weekDate, () => today) : (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; })()
  );
  const [monthDate, setMonthDate] = useState(() =>
    initialViewState ? getInitialDate(initialViewState.monthDate, () => today) : (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; })()
  );
  const [weekDisplayedDate, setWeekDisplayedDate] = useState(() =>
    initialViewState ? getInitialDate(initialViewState.weekDate, () => today) : (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; })()
  );
  const [monthDisplayedDate, setMonthDisplayedDate] = useState(() =>
    initialViewState ? getInitialDate(initialViewState.monthDate, () => today) : (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; })()
  );
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    weekViewOnly ? 'week' : (initialViewState?.viewMode === 'month' ? 'month' : 'week')
  );
  const weekSwiperRef = useRef<SwiperType | null>(null);
  const monthSwiperRef = useRef<SwiperType | null>(null);
  const weekViewRef = useRef<HTMLDivElement>(null);
  const monthViewRef = useRef<HTMLDivElement>(null);
  const DOT_BLINK_DURATION = 1.6;
  const dotDelayRef = useRef(0);
  
  // Get current date based on view mode
  const currentDate = viewMode === 'week' ? weekDate : monthDate;
  const displayedDate = viewMode === 'week' ? weekDisplayedDate : monthDisplayedDate;

  // In month view: hide Today button when today is already in the visible month
  const isTodayInVisibleMonth = viewMode === 'month' &&
    today.getFullYear() === monthDate.getFullYear() &&
    today.getMonth() === monthDate.getMonth();

  // Persist calendar view state so it can be restored when returning from workout details
  useEffect(() => {
    if (!onCalendarStateChange) return;
    const state: CalendarViewState = {
      viewMode,
      weekDate: toLocalDateKey(weekDate),
      monthDate: toLocalDateKey(monthDate),
    };
    onCalendarStateChange(state);
  }, [viewMode, weekDate, monthDate, onCalendarStateChange]);

  // Notify parent when displayed (year, month) or range changes so they can fetch more if outside loaded range
  const lastNotifiedRef = useRef<{ year: number; month: number } | null>(null);
  const lastRangeRef = useRef<{ start: string; end: string } | null>(null);
  useEffect(() => {
    const d = viewMode === 'week' ? weekDate : monthDate;
    const y = d.getFullYear();
    const m = d.getMonth();
    if (viewMode === 'week') {
      const weekDays = getWeekDays(weekDate);
      const rangeStart = toLocalDateKey(weekDays[0]);
      const rangeEnd = toLocalDateKey(weekDays[6]);
      if (onDisplayedRangeChange && (!lastRangeRef.current || lastRangeRef.current.start !== rangeStart || lastRangeRef.current.end !== rangeEnd)) {
        lastRangeRef.current = { start: rangeStart, end: rangeEnd };
        onDisplayedRangeChange(rangeStart, rangeEnd);
      }
    } else {
      const firstDay = new Date(y, m, 1);
      const lastDay = new Date(y, m + 1, 0);
      const rangeStart = toLocalDateKey(firstDay);
      const rangeEnd = toLocalDateKey(lastDay);
      if (onDisplayedRangeChange && (!lastRangeRef.current || lastRangeRef.current.start !== rangeStart || lastRangeRef.current.end !== rangeEnd)) {
        lastRangeRef.current = { start: rangeStart, end: rangeEnd };
        onDisplayedRangeChange(rangeStart, rangeEnd);
      }
    }
    if (onDisplayedMonthChange) {
      const last = lastNotifiedRef.current;
      if (last && last.year === y && last.month === m) return;
      lastNotifiedRef.current = { year: y, month: m };
      onDisplayedMonthChange(y, m);
    }
  }, [viewMode, weekDate, monthDate, onDisplayedMonthChange, onDisplayedRangeChange]);

  // Session status display order: scheduled → in_progress → completed → skipped (others last)
  const SESSION_STATUS_ORDER: Record<string, number> = { scheduled: 0, in_progress: 1, completed: 2, skipped: 3 };

  // Group sessions by date
  const sessionsByDate = sessions.reduce((acc, session) => {
    const dateKey = session.scheduledDate.split('T')[0];
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(session);
    return acc;
  }, {} as Record<string, (WorkoutSession & { workout?: any })[]>);

  // Get sessions for a specific date, sorted by status order
  const getSessionsForDate = (date: Date | null): (WorkoutSession & { workout?: any })[] => {
    if (!date) return [];
    const dateKey = toLocalDateKey(date);
    const list = sessionsByDate[dateKey] || [];
    return [...list].sort((a, b) => (SESSION_STATUS_ORDER[a.status] ?? 99) - (SESSION_STATUS_ORDER[b.status] ?? 99));
  };

  // Check if date is today
  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date has workouts
  const hasWorkouts = (date: Date | null) => {
    return getSessionsForDate(date).length > 0;
  };

  // Check if date has completed workouts
  const hasCompletedWorkouts = (date: Date | null) => {
    if (!date) return false;
    const sessions = getSessionsForDate(date);
    return sessions.some(session => session.status === 'completed');
  };

  // Check if date has scheduled workouts (scheduled or in_progress)
  const hasScheduledWorkouts = (date: Date | null) => {
    if (!date) return false;
    const sessions = getSessionsForDate(date);
    return sessions.some(session => session.status === 'scheduled' || session.status === 'in_progress');
  };

  // Check if date has skipped workouts
  const hasSkippedWorkouts = (date: Date | null) => {
    if (!date) return false;
    const sessions = getSessionsForDate(date);
    return sessions.some(session => session.status === 'skipped');
  };

  // Handle day click — always notify parent (including empty days) so banner can show selected date or empty state
  const handleDayClick = (date: Date | null) => {
    if (!date) return;
    const dateKey = toLocalDateKey(date);
    const daySessions = getSessionsForDate(date);
    onDateClick(dateKey, daySessions);
  };

  // Get 7 days starting from Monday of the week containing date
  const getWeekDays = (date: Date) => {
    const dateCopy = new Date(date);
    const dayOfWeek = dateCopy.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    dateCopy.setDate(dateCopy.getDate() + mondayOffset);
    
    return Array.from({ length: 7 }, (_, i) => {
      const dayDate = new Date(dateCopy);
      dayDate.setDate(dateCopy.getDate() + i);
      return dayDate;
    });
  };

  // Get month view dates
  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  // Memoize weeks and months to prevent regeneration on every slide
  const weeks = useMemo(() => {
    const weeksArray: Date[] = [];
    for (let i = -10; i <= 10; i++) {
      const weekDate = new Date(weekDisplayedDate);
      weekDate.setDate(weekDisplayedDate.getDate() + (i * 7));
      weeksArray.push(weekDate);
    }
    return weeksArray;
  }, [weekDisplayedDate]);

  const months = useMemo(() => {
    const monthsArray: Date[] = [];
    for (let i = -12; i <= 12; i++) {
      const monthDate = new Date(monthDisplayedDate.getFullYear(), monthDisplayedDate.getMonth() + i, 1);
      monthsArray.push(monthDate);
    }
    return monthsArray;
  }, [monthDisplayedDate]);

  // Handle slide change - calculate visible date from index without regenerating arrays
  const handleSlideChange = (swiper: SwiperType, isWeek: boolean) => {
    const activeIndex = swiper.activeIndex;
    const centerIndex = isWeek ? 10 : 12;
    const offset = activeIndex - centerIndex;
    
    // Calculate the visible date from the offset, but don't update displayedDate
    // This prevents array regeneration while still updating the title
    if (isWeek) {
      const visibleDate = new Date(weekDisplayedDate);
      visibleDate.setDate(weekDisplayedDate.getDate() + (offset * 7));
      setWeekDate(visibleDate);
      
      // Only regenerate arrays if we're close to the edges (within 3 slides)
      if (Math.abs(offset) >= 7) {
        setWeekDisplayedDate(visibleDate);
        // Reset to center after regeneration
        setTimeout(() => {
          if (weekSwiperRef.current) {
            weekSwiperRef.current.slideTo(centerIndex, 0);
          }
        }, 0);
      }
    } else {
      const visibleDate = new Date(monthDisplayedDate.getFullYear(), monthDisplayedDate.getMonth() + offset, 1);
      setMonthDate(visibleDate);
      
      // Only regenerate arrays if we're close to the edges (within 3 slides)
      if (Math.abs(offset) >= 9) {
        setMonthDisplayedDate(visibleDate);
        // Reset to center after regeneration
        setTimeout(() => {
          if (monthSwiperRef.current) {
            monthSwiperRef.current.slideTo(centerIndex, 0);
          }
        }, 0);
      }
    }
  };

  // Navigate to today
  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Update the appropriate view's date
    if (viewMode === 'week') {
      setWeekDisplayedDate(today);
      setWeekDate(today);
      setTimeout(() => {
        if (weekSwiperRef.current) {
          weekSwiperRef.current.slideTo(10, 300);
        }
      }, 50);
    } else {
      setMonthDisplayedDate(today);
      setMonthDate(today);
      setTimeout(() => {
        if (monthSwiperRef.current) {
          monthSwiperRef.current.slideTo(12, 300);
        }
      }, 50);
    }
    onTodayClick?.();
  };

  const toggleViewMode = useCallback(() => {
    dotDelayRef.current = (Date.now() / 1000) % DOT_BLINK_DURATION;
    setViewMode(prev => {
      const newMode = prev === 'week' ? 'month' : 'week';
      if (newMode === 'week') {
        setWeekDisplayedDate(weekDate);
      } else {
        setMonthDisplayedDate(monthDate);
      }
      return newMode;
    });
  }, [weekDate, monthDate]);

  const toggleViewModeRef = useRef(toggleViewMode);
  toggleViewModeRef.current = toggleViewMode;

  const isShowingCurrentWeek = useMemo(() => {
    const todayWeek = getWeekDays(today);
    const currentWeek = getWeekDays(weekDate);
    return toLocalDateKey(todayWeek[0]) === toLocalDateKey(currentWeek[0]);
  }, [today, weekDate]);

  useImperativeHandle(ref, () => ({
    viewMode,
    toggleViewMode: () => toggleViewModeRef.current?.(),
    goToToday,
    isShowingCurrentWeek,
  }), [viewMode, isShowingCurrentWeek]);

  // Handle click outside to collapse monthly view
  useEffect(() => {
    if (viewMode === 'month') {
      const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        // Ignore clicks on header controls (view toggle, Today); they have their own handlers
        if (target.closest?.('[data-calendar-toggle]')) return;
        if (target.closest?.('[data-calendar-today]')) return;
        const calendarElement = document.querySelector('[data-calendar-container]');
        if (calendarElement && target && !calendarElement.contains(target)) {
          dotDelayRef.current = (Date.now() / 1000) % DOT_BLINK_DURATION;
          setViewMode('week');
        }
      };

      // Add event listener with a small delay to avoid immediate collapse
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [viewMode]);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Find initial slide index based on the stored date
  const getInitialSlide = (isWeek: boolean) => {
    if (isWeek) {
      // Calculate the offset from weekDisplayedDate to weekDate
      const daysDiff = Math.round((weekDate.getTime() - weekDisplayedDate.getTime()) / (1000 * 60 * 60 * 24));
      const weeksDiff = Math.round(daysDiff / 7);
      const centerIndex = 10;
      const targetIndex = centerIndex + weeksDiff;
      
      // Clamp to valid range
      return Math.max(0, Math.min(20, targetIndex));
    } else {
      // Calculate the offset from monthDisplayedDate to monthDate
      const monthsDiff = (monthDate.getFullYear() - monthDisplayedDate.getFullYear()) * 12 + 
                        (monthDate.getMonth() - monthDisplayedDate.getMonth());
      const centerIndex = 12;
      const targetIndex = centerIndex + monthsDiff;
      
      // Clamp to valid range
      return Math.max(0, Math.min(24, targetIndex));
    }
  };

  // Fixed height for date cells so selected/today boxes and indicators don't get cut off
  const dateCellMinHeight = 'min-h-[3.25rem]';

  // One indicator per session — if 1–2 workouts show both; if 3+ show one icon then "+N"
  // Fixed-width row so single and multiple icons align; all icons and text black
  const iconColorClass = 'text-black dark:text-white';
  const renderDayIndicators = (date: Date) => {
    const daySessions = getSessionsForDate(date);
    if (daySessions.length === 0) return null;
    const sorted = [...daySessions].sort((a, b) => (SESSION_STATUS_ORDER[a.status] ?? 99) - (SESSION_STATUS_ORDER[b.status] ?? 99));
    const toShow = sorted.length <= 2 ? sorted : sorted.slice(0, 1);
    const extraCount = sorted.length <= 2 ? 0 : sorted.length - 1;
    return (
      <div className="flex flex-nowrap items-center justify-center gap-0.5 mt-1.5 w-full min-h-[0.875rem]">
        <div className="flex flex-nowrap items-center justify-center gap-0.5">
          {toShow.map((session) => {
            if (session.status === 'completed') {
              return <Check key={session.id} className={`w-2.5 h-2.5 flex-shrink-0 ${iconColorClass}`} strokeWidth={4} />;
            }
            if (session.status === 'skipped') {
              return <X key={session.id} className={`w-2.5 h-2.5 flex-shrink-0 ${iconColorClass}`} strokeWidth={4} />;
            }
            if (session.status === 'in_progress') {
              return <Play key={session.id} className={`w-2.5 h-2.5 fill-current flex-shrink-0 ${iconColorClass}`} strokeWidth={2.5} />;
            }
            if (session.status === 'scheduled') {
              return (
                <div
                  key={session.id}
                  className="w-2 h-2 flex-shrink-0 rounded-full calendar-cell-dot-scheduled bg-black dark:bg-white"
                  style={{ boxSizing: 'border-box' }}
                />
              );
            }
            return null;
          })}
          {extraCount > 0 && (
            <span className={`text-[10px] font-medium flex-shrink-0 ${iconColorClass}`}>+{extraCount}</span>
          )}
        </div>
      </div>
    );
  };

  // FR-1.7: selected = filled black box with white text; today when not selected = filled grey box with black text
  const isSelected = (d: Date) => selectedDate && toLocalDateKey(d) === selectedDate;
  const isTodayNotSelected = (d: Date) => isToday(d) && selectedDate && toLocalDateKey(d) !== selectedDate;

  // Unified status only when ALL workouts that day have the same status; null when mixed or none (used for cell class + day number color)
  const getUnifiedDayStatus = (date: Date): string | null => {
    const daySessions = getSessionsForDate(date);
    if (daySessions.length === 0) return null;
    const firstStatus = daySessions[0].status;
    return daySessions.every((s) => s.status === firstStatus) ? firstStatus : null;
  };

  // Day number text color: use status color only if ALL workouts that day have the same status; otherwise black
  const getDayNumberTextClass = (date: Date) => {
    const daySessions = getSessionsForDate(date);
    if (daySessions.length === 0) return cellDefaultTextClass;
    const firstStatus = daySessions[0].status;
    const allSame = daySessions.every((s) => s.status === firstStatus);
    if (!allSame) return isLight ? 'text-theme-text' : 'text-white';
    if (firstStatus === 'completed') return 'text-gray-600 dark:text-gray-400';
    if (firstStatus === 'skipped') return 'text-gray-600 dark:text-gray-400';
    if (firstStatus === 'scheduled') return 'text-gray-600 dark:text-gray-400';
    if (firstStatus === 'in_progress') return 'text-gray-600 dark:text-gray-400';
    return isLight ? 'text-theme-text' : 'text-white';
  };

  // Render week day
  const renderWeekDay = (date: Date) => {
    const isTodayDate = isToday(date);
    const unifiedStatus = getUnifiedDayStatus(date);
    const hasScheduled = hasScheduledWorkouts(date);
    const selected = isSelected(date);
    const todayGrey = isTodayNotSelected(date);

    const boxContent = (
      <div
        className={`
          flex items-center justify-center rounded transition-all cursor-pointer
          h-6 w-6 mx-auto
          ${selected ? 'bg-black dark:bg-white' : ''}
          ${todayGrey ? 'bg-white dark:bg-white/95' : ''}
          ${!selected && !todayGrey && (unifiedStatus === 'completed'
            ? 'calendar-cell-completed bg-transparent'
            : unifiedStatus === 'skipped'
              ? 'calendar-cell-skipped bg-transparent'
              : isTodayDate
                ? 'calendar-cell-today bg-white text-theme-text shadow-sm'
                  : unifiedStatus === 'scheduled' || unifiedStatus === 'in_progress' || hasScheduled
                    ? `bg-transparent border-2 ${cellBorderClass} ${cellDefaultTextClass}`
                    : `bg-transparent ${textMutedClass}`)}
        `}
        onClick={() => handleDayClick(date)}
      >
        <span className={`
          text-xs font-semibold
          ${selected ? '!text-white dark:!text-theme-bg-dark' : ''}
          ${todayGrey ? 'text-theme-text dark:text-theme-off-white' : ''}
          ${!selected && !todayGrey ? getDayNumberTextClass(date) : ''}
        `}>
          {date.getDate()}
        </span>
      </div>
    );

    return (
      <div className={`flex flex-col items-center relative overflow-visible ${dateCellMinHeight}`}>
        {boxContent}
        {renderDayIndicators(date)}
      </div>
    );
  };

  // Render month day
  const renderMonthDay = (date: Date | null) => {
    if (date === null) {
      return <div className={`${dateCellMinHeight} w-7 mx-auto`} />;
    }

    const isTodayDate = isToday(date);
    const unifiedStatus = getUnifiedDayStatus(date);
    const hasScheduled = hasScheduledWorkouts(date);
    const selected = isSelected(date);
    const todayGrey = isTodayNotSelected(date);

    const boxContent = (
      <div
        className={`
          flex items-center justify-center rounded transition-all cursor-pointer
          h-6 w-6 mx-auto
          ${selected ? 'bg-black dark:bg-white' : ''}
          ${todayGrey ? 'bg-white dark:bg-white/95' : ''}
          ${!selected && !todayGrey && (unifiedStatus === 'completed'
            ? 'calendar-cell-completed bg-transparent'
            : unifiedStatus === 'skipped'
              ? 'calendar-cell-skipped bg-transparent'
              : isTodayDate
                ? 'calendar-cell-today bg-white text-theme-text shadow-sm'
                  : unifiedStatus === 'scheduled' || unifiedStatus === 'in_progress' || hasScheduled
                    ? `bg-transparent border-2 ${cellBorderClass} ${cellDefaultTextClass}`
                    : `bg-transparent ${textMutedClass}`)}
        `}
        onClick={() => handleDayClick(date)}
      >
        <span className={`
          text-xs font-semibold
          ${selected ? '!text-white dark:!text-theme-bg-dark' : ''}
          ${todayGrey ? 'text-theme-text dark:text-theme-off-white' : ''}
          ${!selected && !todayGrey ? getDayNumberTextClass(date) : ''}
        `}>
          {date.getDate()}
        </span>
      </div>
    );

    return (
      <div className={`flex flex-col items-center relative overflow-visible ${dateCellMinHeight}`}>
        {boxContent}
        {renderDayIndicators(date)}
      </div>
    );
  };

  return (
    <div 
      className={sticky ? 'sticky top-0 z-40 relative' : 'relative'}
      data-calendar-container
    >
      {/* Header: left = month name (month view only), right = Today + calendar view toggle (hidden when hideHeaderRow) */}
      {!hideHeaderRow && (
        <div className="flex items-center justify-between px-4 py-3">
          <div className="min-w-0 flex-1">
            {viewMode === 'month' && (
              <h2 className={`text-base font-medium ${textClass}`}>
                {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
              </h2>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(viewMode === 'week' || !isTodayInVisibleMonth) && (
              <button
                onClick={goToToday}
                className={todayBtnClass}
              >
                Today
              </button>
            )}
            {!weekViewOnly && (
              <button
                type="button"
                data-calendar-toggle
                onClick={(e) => { e.stopPropagation(); toggleViewMode(); }}
                className="p-1.5 flex items-center justify-center rounded-lg transition-opacity hover:opacity-80 text-white"
                aria-label={viewMode === 'week' ? 'Expand to monthly view' : 'Collapse to weekly view'}
              >
                {viewMode === 'week' ? (
                  <Calendar className="w-5 h-5" aria-hidden />
                ) : (
                  <WeekViewIcon className="w-5 h-5" size={20} aria-hidden />
                )}
              </button>
            )}
          </div>
        </div>
      )}

        {/* Static day names - always visible */}
        <div className="px-4 pb-1">
          <div className="grid grid-cols-7 gap-2 mb-1.5">
            {dayNames.map((day) => (
              <div
                key={day}
                className={`text-center text-[10px] font-medium uppercase py-1 ${textMutedClass}`}
              >
                {day.substring(0, 3)}
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Container */}
        <div className="relative pb-2">
          {/* Week View */}
          <AnimatePresence mode="wait">
            {viewMode === 'week' && (
              <motion.div
                key="week-view"
                ref={weekViewRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ 
                  height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.2, ease: 'easeInOut' }
                }}
                className="overflow-visible relative"
              >
              {/* Swiper for weeks – .week-view-swiper viewport has overflow-x: hidden in CSS to hide adjacent weeks */}
              <div className="px-4 pb-0">
                <Swiper
                  className="week-view-swiper w-full"
                  onSwiper={(swiper: SwiperType) => {
                    weekSwiperRef.current = swiper;
                    // Use requestAnimationFrame to ensure weeks array is ready
                    requestAnimationFrame(() => {
                      const initialIndex = getInitialSlide(true);
                      swiper.slideTo(initialIndex, 0);
                    });
                  }}
                  onSlideChange={(swiper: SwiperType) => handleSlideChange(swiper, true)}
                  modules={[Navigation, Pagination]}
                  spaceBetween={0}
                  slidesPerView={1}
                  allowTouchMove={true}
                  resistance={true}
                  resistanceRatio={0.85}
                  style={{ width: '100%' }}
                >
                  {weeks.map((weekDate, index) => {
                    const weekDays = getWeekDays(weekDate);
                    return (
                      <SwiperSlide key={`week-${index}`} style={{ width: '100%', overflow: 'visible' }}>
                        <div className="grid grid-cols-7 gap-2 w-full overflow-visible pb-3">
                          {weekDays.map((date, i) => (
                            <React.Fragment key={`${index}-${i}`}>
                              {renderWeekDay(date)}
                            </React.Fragment>
                          ))}
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

          {/* Month View */}
          <AnimatePresence mode="wait">
            {viewMode === 'month' && (
              <motion.div
                key="month-view"
                ref={monthViewRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ 
                  height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.2, ease: 'easeInOut' }
                }}
                className="overflow-visible relative"
              >
                <div className="px-4 pb-2">
                <Swiper
                  onSwiper={(swiper: SwiperType) => {
                    monthSwiperRef.current = swiper;
                    // Use requestAnimationFrame to ensure months array is ready
                    requestAnimationFrame(() => {
                      const initialIndex = getInitialSlide(false);
                      swiper.slideTo(initialIndex, 0);
                    });
                  }}
                  onSlideChange={(swiper: SwiperType) => handleSlideChange(swiper, false)}
                  modules={[Navigation, Pagination]}
                  spaceBetween={0}
                  slidesPerView={1}
                  allowTouchMove={true}
                  resistance={true}
                  resistanceRatio={0.85}
                  className="w-full"
                  style={{ width: '100%' }}
                >
                  {months.map((monthDate, index) => {
                    const monthDays = getMonthDays(monthDate);
                    return (
                      <SwiperSlide key={`month-${index}`} style={{ width: '100%' }}>
                        <div className="grid grid-cols-7 gap-2 w-full">
                          {monthDays.map((date, i) => (
                            <React.Fragment key={`${index}-${i}`}>
                              {renderMonthDay(date)}
                            </React.Fragment>
                          ))}
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
    </div>
  );
});
